import { useCallback, useEffect, useState } from "react";
import { Download, Trash2, Loader2, MessageCircle, ExternalLink } from "lucide-react";
import { api, BACKEND, waLink } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "converted", "closed"];
const STATUS_STYLE = {
  new: "text-rose-400 bg-rose-400/10 border-rose-400/30",
  contacted: "text-amber-400 bg-amber-400/10 border-amber-400/30",
  converted: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  closed: "text-subtle bg-white/5 border-white/15",
};

export default function LeadsManager() {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("");
  const [noteDrafts, setNoteDrafts] = useState({});

  const load = useCallback(() => {
    api.get("/admin/leads", { params: filter ? { status: filter } : {} }).then((r) => setLeads(r.data)).catch(() => toast.error("Failed to load leads"));
  }, [filter]);

  useEffect(load, [load]);

  const update = async (id, payload) => {
    try {
      await api.patch(`/admin/leads/${id}`, payload);
      load();
      if (payload.status) toast.success(`Marked ${payload.status}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const saveNote = async (id) => {
    const note = noteDrafts[id];
    if (note === undefined) return;
    try {
      await api.patch(`/admin/leads/${id}`, { notes: note });
      toast.success("Note saved");
      setNoteDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
    } catch (e) {
      toast.error("Note save failed");
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
    await api.delete(`/admin/leads/${lead.id}`).catch(() => toast.error("Delete failed"));
    toast.success("Lead deleted");
    load();
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "scalovix-leads.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight">Leads Manager</h1>
          <p className="text-xs text-subtle mt-1.5">Every enquiry from the website form lands here.</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent-bright text-white font-semibold px-5 py-2.5 text-xs transition-all" data-testid="admin-leads-export-csv-btn">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {[{ v: "", l: "All" }, ...STATUSES.map((s) => ({ v: s, l: s.charAt(0).toUpperCase() + s.slice(1) }))].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all duration-300 ${filter === f.v ? "bg-accent border-accent text-white" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
            data-testid={`admin-leads-filter-${f.v || "all"}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {!leads ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 text-accent-bright animate-spin" /></div>
      ) : leads.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-sm text-subtle" data-testid="admin-leads-empty">
          No leads {filter ? `with status "${filter}"` : "yet"} — they'll appear here the moment someone submits the contact form.
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((lead, i) => (
            <div key={lead.id} className="glass rounded-2xl p-5" data-testid={`admin-lead-row-${i}`}>
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <p className="text-sm font-semibold text-white">{lead.name}</p>
                    <span className={`font-mono-label text-[8px] rounded-full px-2.5 py-1 border ${STATUS_STYLE[lead.status] || ""}`} data-testid={`admin-lead-status-badge-${i}`}>{lead.status}</span>
                    <span className="font-mono-label text-[8px] text-subtle">{String(lead.source || "contact-form")}</span>
                  </div>
                  <p className="text-xs text-subtle mt-1.5">
                    {new Date(lead.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    {lead.service ? ` • ${lead.service}` : ""}
                  </p>
                  {lead.message && <p className="text-xs text-mist mt-2 leading-relaxed">{lead.message}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <a href={`tel:${lead.phone}`} className="p-2 rounded-lg border border-white/10 text-mist hover:text-white transition-colors" data-testid={`admin-lead-call-${i}`} aria-label="Call"><ExternalLink className="h-4 w-4" /></a>
                  <a href={waLink(lead.phone, `Hi ${lead.name}, following up on your enquiry!`)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg border border-white/10 text-mist hover:text-emerald-400 transition-colors" data-testid={`admin-lead-whatsapp-${i}`} aria-label="WhatsApp">
                    <MessageCircle className="h-4 w-4" />
                  </a>
                  <button onClick={() => remove(lead)} className="p-2 rounded-lg text-subtle hover:text-rose-400 hover:bg-rose-500/10 transition-colors" data-testid={`admin-lead-delete-${i}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <select
                  value={lead.status}
                  onChange={(e) => update(lead.id, { status: e.target.value })}
                  className="bg-[#131327] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-accent/60"
                  data-testid={`admin-lead-status-select-${i}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}
                </select>
                <input
                  defaultValue={lead.notes || ""}
                  onChange={(e) => setNoteDrafts((d) => ({ ...d, [lead.id]: e.target.value }))}
                  onBlur={() => saveNote(lead.id)}
                  placeholder="Internal notes — auto-saves when you click away..."
                  className="flex-1 bg-[#131327] border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-subtle outline-none focus:border-accent/60"
                  data-testid={`admin-lead-notes-input-${i}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
